import { BOOKS, bookById } from "./canon.ts";

/** A contiguous passage inside one book. Start and end are inclusive. */
export interface Passage {
  bookId: number;
  startChapter: number;
  startVerse: number;
  endChapter: number;
  endVerse: number;
}

export interface VerseRef {
  bookId: number;
  chapter: number;
  verse: number;
}

const PSALMS_ID = BOOKS.find((b) => b.name === "Psalms")!.id;
const isPsalms = (bookId: number | null | undefined) => bookId === PSALMS_ID;

/**
 * Psalms is the one book whose divisions are individually named, so it has no "chapters": the whole book and
 * several psalms are "Psalms", but one psalm is "a Psalm" ("Psalm 23", "Psalm 129:6-7").
 */
export function passageBookName(p: Passage): string {
  return isPsalms(p.bookId) && p.startChapter === p.endChapter ? "Psalm" : bookById(p.bookId).name;
}

/** What to call the numbered division of a book: "Psalm" for Psalms, "Chapter" for everything else. */
export function chapterLabel(bookId: number | null | undefined): "Psalm" | "Chapter" {
  return isPsalms(bookId) ? "Psalm" : "Chapter";
}

/** "Genesis 1", "Psalm 23": one chapter (one psalm) named in full. */
export function chapterName(bookId: number, chapter: number): string {
  return `${isPsalms(bookId) ? "Psalm" : bookById(bookId).name} ${chapter}`;
}

function render(name: string, p: Passage): string {
  const start = `${p.startChapter}:${p.startVerse}`;
  if (p.startChapter === p.endChapter) {
    return p.startVerse === p.endVerse ? `${name} ${start}` : `${name} ${start}-${p.endVerse}`;
  }
  return `${name} ${start}-${p.endChapter}:${p.endVerse}`;
}

/** For people: "John 3:16", "John 3:16-17", "Genesis 1:31-2:3", "Psalm 129:6-7", "Psalms 22:31-23:3". */
export function formatPassage(p: Passage): string {
  return render(passageBookName(p), p);
}

/**
 * The stable identity of a passage, e.g. "Psalms 129:6-7". Content files (data/content/trivia) are keyed by it,
 * so it must never follow display wording: change formatPassage freely, but not this.
 */
export function passageKey(p: Passage): string {
  return render(bookById(p.bookId).name, p);
}

/** -1 / 0 / 1 ordering of two verse positions within the same book. */
export function compareVerse(a: Pick<VerseRef, "chapter" | "verse">, b: Pick<VerseRef, "chapter" | "verse">): number {
  return a.chapter - b.chapter || a.verse - b.verse;
}

export function passageContains(p: Passage, v: VerseRef): boolean {
  if (p.bookId !== v.bookId) return false;
  return (
    compareVerse({ chapter: p.startChapter, verse: p.startVerse }, v) <= 0 &&
    compareVerse(v, { chapter: p.endChapter, verse: p.endVerse }) <= 0
  );
}
